import type { ViewApi } from '@fullcalendar/core';
import type FullCalendar from '@fullcalendar/react';
import { useAuthentication } from '@src/features/auth';
import settings from '@src/shared/config';
import { getServerErrorMessage } from '@src/shared/lib';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildLockReleaseRequest,
  findAbsenceEditor,
  getInitialVisibleRange,
  getSessionId,
} from '../../lib';
import type {
  AbsenceLockState,
  SelectedAbsence,
  TimelineSlot,
  VisibleRange,
} from '../../types';
import {
  ABSENCE_LOCK_STATUS,
  isAbsenceLockErrorStatus,
  isAbsenceLockHeldStatus,
} from '../../types';
import { type SidebarMode, TimelineContext } from '../context';
import { useAbsenceLock } from '../query';
import { absenceLockReleaseUrl } from '../query/constants';
import {
  getAbsenceConnectionId,
  useAbsenceConnectionId,
  useAbsenceEditing,
} from '../realtime';

const initialLockState: AbsenceLockState = {
  status: ABSENCE_LOCK_STATUS.IDLE,
  lock: null,
  message: null,
};

export default function TimelineProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [view, setView] = useState<ViewApi | null>(null);
  const [selectedAbsence, setSelectedAbsence] =
    useState<SelectedAbsence | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimelineSlot | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(null);
  const [suppressSelectionUntil, setSuppressSelectionUntil] = useState(0);
  // A task reference lands here as `?absenceId=&date=`, and the absence is
  // usually in another month. Seeding the range from that date lets the
  // calendar mount on the right month instead of being moved there
  // imperatively a frame later -- one less re-render in the burst that
  // ScrollGrid's layout budget has to survive (see SCROLLGRID_SETTLE_MS).
  const { date: routeDate } = useSearch({ strict: false }) as { date?: string };
  const [initialVisibleRange] = useState<VisibleRange>(() =>
    getInitialVisibleRange(routeDate),
  );
  const [visibleRange, setVisibleRange] =
    useState<VisibleRange>(initialVisibleRange);
  const initialCalendarDate = initialVisibleRange.start;
  const [absenceLock, setAbsenceLock] =
    useState<AbsenceLockState>(initialLockState);
  const [showLegend, setShowLegend] = useState(false);
  const [showBalanceHistory, setShowBalanceHistory] = useState(false);
  const calendarRef = useRef<FullCalendar | null>(null);
  const { acquireLock, refreshLock, releaseLock } = useAbsenceLock();
  const { credentials, user } = useAuthentication();
  const lockRef = useRef(absenceLock);
  // Which connection the lock in hand was claimed under. The server ties the
  // lock to that socket, so a difference from the live id means the lock it
  // held is gone. State rather than a ref because the reclaim effect below is
  // driven by it: a lock landing bound to a connection that has already been
  // replaced has to re-arm that effect, and it cannot key off the lock status
  // instead -- it sets ACQUIRING itself, so status would re-run it and the
  // cleanup would cancel the very request it had just started.
  const [boundConnectionId, setBoundConnectionId] = useState('');
  const activeLockTargetUserId =
    sidebarMode === 'schedule' || sidebarMode === 'withdraw'
      ? selectedSlot?.userId ?? null
      : sidebarMode === 'details'
        ? selectedAbsence?.userId ?? null
        : null;
  const previousUserIdRef = useRef<number | null>(activeLockTargetUserId);
  const editingLocks = useAbsenceEditing();
  const connectionId = useAbsenceConnectionId();
  // The snapshot entry for the user in view, whoever holds it. The *rival*
  // below is derived from this, but the entry itself is what effects depend
  // on: a release by this user's own other tab changes the entry while
  // leaving the rival null, and depending on the rival alone would miss it.
  const editingEntry = findAbsenceEditor(editingLocks, activeLockTargetUserId);
  // Their own lock is in the snapshot too. Counting it as a rival is what
  // would strand them after a heartbeat blip: the status goes EXPIRED and the
  // re-acquire that should heal it would be blocked by the very lock it is
  // reclaiming.
  const viewerId = user?.id ?? null;
  const contestedBy =
    editingEntry && editingEntry.ownerUserId !== viewerId ? editingEntry : null;

  useEffect(() => {
    lockRef.current = absenceLock;
  }, [absenceLock]);

  const setIdleLockState = useCallback(() => {
    setAbsenceLock(initialLockState);
  }, []);

  /**
   * Record which connection a lock was claimed under.
   *
   * Truthfully: what the server was told, not whatever id happens to be live
   * when the request resolves. If the socket reconnected mid-flight the two
   * differ, and that difference is what the reclaim effect keys off -- without
   * it the form stays editable over a lock the disconnect has already deleted.
   */
  const rememberLockConnection = useCallback((claimedUnder: string) => {
    setBoundConnectionId(claimedUnder);
  }, []);

  const releaseCurrentLock = useCallback(
    async (lockedUserId?: number | null) => {
      const lock = lockRef.current.lock;
      if (!lock) {
        setIdleLockState();
        return;
      }
      if (lockedUserId != null && lock.userId !== lockedUserId) {
        return;
      }

      try {
        await releaseLock({
          acti_user: lock.userId,
          session_id: getSessionId(),
        });
      } catch {
      } finally {
        if (
          lockedUserId == null ||
          lockRef.current.lock?.userId === lockedUserId
        ) {
          setIdleLockState();
        }
      }
    },
    [releaseLock, setIdleLockState],
  );

  useEffect(() => {
    const nextUserId = activeLockTargetUserId;
    const previousUserId = previousUserIdRef.current;
    const currentLock = lockRef.current.lock;
    const currentStatus = lockRef.current.status;

    if (
      nextUserId === previousUserId &&
      (nextUserId === null ||
        currentStatus === ABSENCE_LOCK_STATUS.ACQUIRING ||
        isAbsenceLockErrorStatus(currentStatus) ||
        (isAbsenceLockHeldStatus(currentStatus) &&
          currentLock?.userId === nextUserId))
    ) {
      return;
    }

    previousUserIdRef.current = nextUserId;
    let isCancelled = false;

    const syncLock = async () => {
      if (currentLock && currentLock.userId !== nextUserId) {
        await releaseCurrentLock(currentLock.userId);
      }

      if (isCancelled) {
        return;
      }

      if (!nextUserId) {
        setIdleLockState();
        return;
      }

      if (
        lockRef.current.lock?.userId === nextUserId &&
        isAbsenceLockHeldStatus(lockRef.current.status)
      ) {
        return;
      }

      setAbsenceLock({
        status: ABSENCE_LOCK_STATUS.ACQUIRING,
        lock: null,
        message: 'Securing this user for editing...',
      });

      try {
        // Read once and reused below. Re-reading it after the request would
        // record whichever id happened to be current when it resolved, which
        // is not the one the server bound the lock to if the socket
        // reconnected in between.
        const claimedUnder = getAbsenceConnectionId();
        const lock = await acquireLock({
          acti_user: nextUserId,
          session_id: getSessionId(),
          connection_id: claimedUnder,
        });
        if (isCancelled) {
          // Named by lease, so this cannot delete an acquisition made after
          // it. A same-session re-acquire keeps the same row, so naming the
          // row would not have distinguished them -- and a release saying
          // only "this session" would take whichever came last, leaving the
          // pane editable over nothing.
          await releaseLock({
            acti_user: lock.userId,
            session_id: getSessionId(),
            lease: lock.lease,
          }).catch(() => undefined);
          return;
        }
        rememberLockConnection(claimedUnder);
        setAbsenceLock({
          status: ABSENCE_LOCK_STATUS.LOCKED,
          lock,
          message: null,
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }
        const baseMessage = getServerErrorMessage(error);
        const lockedBy =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { data?: { locked_by?: unknown } } }).response
            ?.data?.locked_by;
        const message =
          typeof lockedBy === 'string' && lockedBy
            ? `${baseMessage} \n Locked by: ${lockedBy}.`
            : baseMessage;
        setAbsenceLock({
          status: ABSENCE_LOCK_STATUS.CONFLICTED,
          lock: null,
          message,
        });
      }
    };

    void syncLock();

    return () => {
      isCancelled = true;
    };
  }, [
    acquireLock,
    activeLockTargetUserId,
    releaseCurrentLock,
    releaseLock,
    rememberLockConnection,
    setIdleLockState,
  ]);

  useEffect(() => {
    const lock = absenceLock.lock;
    if (!isAbsenceLockHeldStatus(absenceLock.status) || !lock) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void refreshLock({
        acti_user: lock.userId,
        session_id: getSessionId(),
      })
        .then((refreshedLock) => {
          setAbsenceLock((current) => {
            if (current.lock?.userId !== lock.userId) {
              return current;
            }
            return {
              status: ABSENCE_LOCK_STATUS.LOCKED,
              lock: refreshedLock,
              message: null,
            };
          });
        })
        .catch((error) => {
          setAbsenceLock((current) => {
            if (current.lock?.userId !== lock.userId) {
              return current;
            }
            return {
              status: ABSENCE_LOCK_STATUS.EXPIRED,
              lock: null,
              message: getServerErrorMessage(error),
            };
          });
        });
    }, lock.heartbeatIntervalSeconds * 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [absenceLock.lock, absenceLock.status, refreshLock]);

  // Asked again the moment the holder lets go, rather than every two seconds
  // on the chance that they have. `absence.editing` carries the whole set of
  // held locks, so this employee dropping out of it *is* the release -- which
  // is why the dependency is the snapshot entry rather than the rival derived
  // from it: a release by this user's own other tab leaves the rival null
  // throughout and would never re-arm this.
  useEffect(() => {
    if (
      !activeLockTargetUserId ||
      !isAbsenceLockErrorStatus(absenceLock.status) ||
      lockRef.current.lock
    ) {
      return undefined;
    }

    // Derived here rather than taken from the component's `contestedBy`, so
    // the snapshot entry is what this effect actually depends on. A release by
    // this user's own other tab changes the entry while leaving the rival null
    // throughout, and depending on the rival would never re-arm the retry.
    const heldByAnother =
      editingEntry !== null && editingEntry.ownerUserId !== viewerId;
    if (heldByAnother) {
      return undefined;
    }

    const retryUserId = activeLockTargetUserId;
    const claimedUnder = getAbsenceConnectionId();
    let isCancelled = false;

    void acquireLock({
      acti_user: retryUserId,
      session_id: getSessionId(),
      connection_id: claimedUnder,
    })
      .then((lock) => {
        // The pane may have closed or moved to somebody else while this was
        // in flight. Nothing is watching this lock now, so hand it straight
        // back rather than leaving it to expire on an employee nobody is
        // editing.
        if (isCancelled) {
          void releaseLock({
            acti_user: lock.userId,
            session_id: getSessionId(),
            lease: lock.lease,
          }).catch(() => undefined);
          return;
        }
        // Recorded outside the updater: React may run an updater twice, and
        // it is a place for deriving state rather than for setting other
        // state. The lock was acquired either way, so what it is bound to is
        // true whether or not the guard below installs it.
        rememberLockConnection(claimedUnder);
        setAbsenceLock((current) => {
          if (
            !isAbsenceLockErrorStatus(current.status) ||
            lock.userId !== retryUserId
          ) {
            return current;
          }
          return {
            status: ABSENCE_LOCK_STATUS.LOCKED,
            lock,
            message: null,
          };
        });
      })
      .catch(() => {
        // Somebody took it between the release and this call. The next
        // snapshot names them, and this effect runs again when they let go.
      });

    return () => {
      isCancelled = true;
    };
  }, [
    absenceLock.status,
    acquireLock,
    activeLockTargetUserId,
    editingEntry,
    releaseLock,
    rememberLockConnection,
    viewerId,
  ]);

  useEffect(() => {
    return () => {
      const lock = lockRef.current.lock;
      if (!lock) {
        return;
      }
      void releaseLock({
        acti_user: lock.userId,
        session_id: getSessionId(),
      }).catch(() => undefined);
    };
  }, [releaseLock]);

  // A reconnection means a new socket, and the server released whatever the
  // old one was holding when it closed. What this client believes it holds is
  // therefore only a memory: the form has to stop being editable until the
  // lock is actually reclaimed, or somebody else can take the employee while
  // it still looks locked here and the first save fails out of nowhere.
  useEffect(() => {
    if (!connectionId) {
      return undefined;
    }
    const lock = lockRef.current.lock;
    if (
      !isAbsenceLockHeldStatus(lockRef.current.status) ||
      !lock ||
      boundConnectionId === connectionId
    ) {
      return undefined;
    }

    const targetUserId = lock.userId;
    // Reclaiming is only worth anything while that employee is still the one
    // in view. Without this the request outlives the pane, and a success would
    // reinstall -- and go on heartbeating -- a lock for an employee nobody has
    // open.
    if (targetUserId !== activeLockTargetUserId) {
      return undefined;
    }
    let isCancelled = false;

    setAbsenceLock({
      status: ABSENCE_LOCK_STATUS.ACQUIRING,
      lock: null,
      message: 'Reconnecting...',
    });

    void acquireLock({
      acti_user: targetUserId,
      session_id: getSessionId(),
      connection_id: connectionId,
    })
      .then((reclaimed) => {
        if (isCancelled) {
          void releaseLock({
            acti_user: reclaimed.userId,
            session_id: getSessionId(),
            lease: reclaimed.lease,
          }).catch(() => undefined);
          return;
        }
        rememberLockConnection(connectionId);
        setAbsenceLock({
          status: ABSENCE_LOCK_STATUS.LOCKED,
          lock: reclaimed,
          message: null,
        });
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        // Somebody took the employee while this client was away. Saying so is
        // the whole point -- the alternative is an editable form over a lock
        // that no longer exists.
        setAbsenceLock({
          status: ABSENCE_LOCK_STATUS.EXPIRED,
          lock: null,
          message: getServerErrorMessage(error),
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [
    acquireLock,
    activeLockTargetUserId,
    boundConnectionId,
    connectionId,
    releaseLock,
    rememberLockConnection,
  ]);

  // The cleanup above does not run when the tab is closed -- React never
  // unmounts. Without this the lock sits until its TTL lapses, and whoever
  // wanted that employee next is told they are "locked by" somebody who has
  // already gone. `pagehide` fires where unmount does not; see
  // ``buildLockReleaseRequest`` for why it cannot be a beacon.
  useEffect(() => {
    if (!isAbsenceLockHeldStatus(absenceLock.status)) {
      return undefined;
    }

    const releaseOnUnload = () => {
      const lock = lockRef.current.lock;
      if (!lock) {
        return;
      }
      const request = buildLockReleaseRequest({
        targetUserId: lock.userId,
        sessionId: getSessionId(),
        accessToken: credentials?.access ?? null,
        baseUrl: settings.CLIENTS.origin.baseURL,
        path: absenceLockReleaseUrl,
      });
      if (!request) {
        return;
      }
      // Nothing to await and nowhere to report to: the page is going.
      void fetch(...request).catch(() => undefined);
    };

    window.addEventListener('pagehide', releaseOnUnload);
    return () => {
      window.removeEventListener('pagehide', releaseOnUnload);
    };
  }, [absenceLock.status, credentials?.access]);

  useEffect(() => {
    if (!showBalanceHistory) {
      return;
    }

    if (selectedAbsence || selectedSlot || sidebarMode || showLegend) {
      setShowBalanceHistory(false);
    }
  }, [
    selectedAbsence,
    selectedSlot,
    showBalanceHistory,
    showLegend,
    sidebarMode,
  ]);

  const closeEditForm = useCallback(async () => {
    const lockedUserId = lockRef.current.lock?.userId ?? null;
    await releaseCurrentLock(lockedUserId);
    previousUserIdRef.current = null;
    setSidebarMode(null);
    setSelectedAbsence(null);
    setSelectedSlot(null);
  }, [releaseCurrentLock]);

  const closeAbsenceDetails = useCallback(async () => {
    const lockedUserId = lockRef.current.lock?.userId ?? null;
    await releaseCurrentLock(lockedUserId);
    previousUserIdRef.current = null;
    setSidebarMode(null);
    setSelectedAbsence(null);
    setSelectedSlot(null);
  }, [releaseCurrentLock]);

  const openLegend = useCallback(() => setShowLegend(true), []);
  const closeLegend = useCallback(() => setShowLegend(false), []);
  const openBalanceHistory = useCallback(() => setShowBalanceHistory(true), []);
  const closeBalanceHistory = useCallback(
    () => setShowBalanceHistory(false),
    [],
  );

  const value = useMemo(
    () => ({
      view,
      setView,
      selectedAbsence,
      setSelectedAbsence,
      selectedSlot,
      setSelectedSlot,
      selectedUserId,
      setSelectedUserId,
      sidebarMode,
      setSidebarMode,
      suppressSelectionUntil,
      setSuppressSelectionUntil,
      closeEditForm,
      calendarRef,
      initialCalendarDate,
      visibleRange,
      setVisibleRange,
      absenceLock,
      absenceEditor: contestedBy,
      setAbsenceLock,
      showLegend,
      openLegend,
      closeLegend,
      showBalanceHistory,
      openBalanceHistory,
      closeBalanceHistory,
      closeAbsenceDetails,
    }),
    [
      absenceLock,
      contestedBy,
      closeEditForm,
      view,
      initialCalendarDate,
      selectedAbsence,
      selectedSlot,
      selectedUserId,
      sidebarMode,
      suppressSelectionUntil,
      visibleRange,
      showLegend,
      openLegend,
      closeLegend,
      showBalanceHistory,
      openBalanceHistory,
      closeBalanceHistory,
      closeAbsenceDetails,
    ],
  );

  return <TimelineContext value={value}>{children}</TimelineContext>;
}
